import { useEffect } from 'react';
import {
  Control,
  Controller,
  UseFieldArrayReturn,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import dayjs from 'dayjs';
import get from 'lodash/get';
import { ArrowRightIcon, CircleXIcon, Plus } from 'lucide-react';

import { AddDateDetailsButton } from 'components/AddDateDetailsButton';
import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useIsTqlInbox } from 'hooks/useIsTqlInbox';
import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  MultiStopBetaTooltip,
  QUICK_QUOTE_MULTI_STOP_BETA_TOOLTIP,
} from 'pages/QuoteView/LoadBuilding/components/MultiStopBetaTooltip';
import { MaybeUndef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { Quoting } from 'types/enums/Integrations';

import { QuickQuoteDatePicker } from '../common/QuickQuoteDatePicker';
import { QuickQuoteLocationInput } from '../common/QuickQuoteLocationInput';
import { KencoUrgency, KencoUrgencyLabel, QuickQuoteInputs } from '../types';

interface StopsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  fields: UseFieldArrayReturn<QuickQuoteInputs, 'stops'>['fields'];
  remove: UseFieldArrayReturn<QuickQuoteInputs, 'stops'>['remove'];
  isMultiStopQuickQuoteEnabled: boolean;
  hasAddedQuoteDates: boolean;
  setHasAddedQuoteDates: (value: boolean) => void;
  handleAddStop: () => void;
}

export const StopsSection = ({
  formMethods,
  fields,
  remove,
  isMultiStopQuickQuoteEnabled,
  hasAddedQuoteDates,
  setHasAddedQuoteDates,
  handleAddStop,
}: StopsSectionProps) => {
  const { control } = formMethods;
  const { quotingIntegrations, quickQuoteConfig } = useServiceFeatures();
  const isTql = useIsTqlInbox();
  const pickupLabel = isTql ? 'Pick' : 'Pickup';
  const dropoffLabel = isTql ? 'Drop' : 'Dropoff';

  const isFTLQuickQuoteDatesRequired =
    quickQuoteConfig?.isFTLQuickQuoteDatesRequired ?? false;

  const hasKencoFlashIntegration = quotingIntegrations?.some(
    (integration) => integration.name === Quoting.KencoFlash
  );

  const watchedPickupDate = useWatch({ control, name: 'pickupDate' });
  const showKencoUrgency =
    hasKencoFlashIntegration &&
    shouldShowKencoUrgencyForPickup(watchedPickupDate);

  if (!isMultiStopQuickQuoteEnabled) {
    return (
      <TwoStopQuoteSection
        formMethods={formMethods}
        hasAddedQuoteDates={hasAddedQuoteDates}
        setHasAddedQuoteDates={setHasAddedQuoteDates}
        showKencoUrgency={showKencoUrgency}
        isFTLQuickQuoteDatesRequired={isFTLQuickQuoteDatesRequired}
      />
    );
  }

  return (
    <Flex direction='col' gap='sm' className='w-full mx-0'>
      {/* Pickup */}
      <Grid cols='1' gap='xs' className='w-full mx-0'>
        <div className='text-md text-neutral-700 font-semibold'>
          {pickupLabel}
        </div>

        <Grid cols='5' gap='xs' className='w-full mx-0'>
          <div className='col-span-3'>
            <QuickQuoteLocationInput
              name={`stops.0.location`}
              label='Location'
              placeholder='City, State or ZIP'
              required
            />
          </div>

          <Flex direction='col' className='w-full col-span-2'>
            <Label
              name='pickupDate'
              aiIconOnly
              required={isFTLQuickQuoteDatesRequired}
            >
              Date
              {!isFTLQuickQuoteDatesRequired && (
                <Typography variant='body-xs' className='ml-1'>
                  (optional)
                </Typography>
              )}
            </Label>
            <QuickQuoteDatePicker
              name='pickupDate'
              rules={
                isFTLQuickQuoteDatesRequired ? { required: 'Required' } : {}
              }
              className='mt-1 pl-2'
              calendarClassName='mr-1.5'
            />
          </Flex>
        </Grid>
      </Grid>

      {/* Middle stops */}
      {fields.slice(1, -1).map((field, index) => (
        <div key={field.id} className='grid gap-1.5 grid-cols-1 w-full mx-0'>
          <Flex justify='between' align='center' className='w-full mx-0'>
            <div className='text-md text-neutral-700 font-semibold'>
              Stop {index + 1}
            </div>
            <button
              title='Remove stop'
              onClick={() => remove(index + 1)}
              className='flex items-center justify-center rounded hover:text-destructive transition-colors bg-transparent m-0 p-0 min-h-0 min-w-0 h-auto w-auto'
              type='button'
            >
              <CircleXIcon className='w-4 h-4' />
            </button>
          </Flex>

          <div className='col-span-3'>
            <QuickQuoteLocationInput
              name={`stops.${index + 1}.location`}
              label='Location'
              placeholder='City, State or ZIP'
              required
            />
          </div>
        </div>
      ))}

      {/* Add Stop button */}
      <Flex justify='center' className='w-full mt-1'>
        <MultiStopBetaTooltip message={QUICK_QUOTE_MULTI_STOP_BETA_TOOLTIP}>
          <Button
            buttonNamePosthog={ButtonNamePosthog.QuickQuoteAddStop}
            type='button'
            variant='ghost'
            onClick={handleAddStop}
            className='w-fit text-xs text-neutral-800 pl-1.5 hover:border-neutral-700 hover:bg-neutral-100 h-8 px-2.5'
            size='sm'
          >
            <Plus className='h-4 w-4 mr-1' />
            Add Stop
          </Button>
        </MultiStopBetaTooltip>
      </Flex>

      {/* Dropoff */}
      <Grid cols='1' gap='xs' className='w-full mx-0'>
        <div className='text-md text-neutral-700 font-semibold'>
          {dropoffLabel}
        </div>

        <Grid cols='5' gap='xs' className='w-full mx-0'>
          <div className='col-span-3'>
            <QuickQuoteLocationInput
              name={`stops.${fields.length - 1}.location`}
              label='Location'
              placeholder='City, State or ZIP'
              required
            />
          </div>

          <Flex direction='col' className='w-full col-span-2'>
            <Label
              name='deliveryDate'
              aiIconOnly
              required={isFTLQuickQuoteDatesRequired}
            >
              Date
              {!isFTLQuickQuoteDatesRequired && (
                <Typography variant='body-xs' className='ml-1'>
                  (optional)
                </Typography>
              )}
            </Label>
            <QuickQuoteDatePicker
              name='deliveryDate'
              rules={
                isFTLQuickQuoteDatesRequired ? { required: 'Required' } : {}
              }
              className='mt-1 pl-2'
              calendarClassName='mr-1.5'
            />
          </Flex>
        </Grid>
      </Grid>

      {showKencoUrgency && <KencoUrgencyPicker control={control} />}
    </Flex>
  );
};

interface TwoStopQuoteSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  hasAddedQuoteDates: boolean;
  setHasAddedQuoteDates: (value: boolean) => void;
  showKencoUrgency: boolean;
  isFTLQuickQuoteDatesRequired: boolean;
}

const TwoStopQuoteSection = ({
  formMethods,
  hasAddedQuoteDates,
  setHasAddedQuoteDates,
  showKencoUrgency,
  isFTLQuickQuoteDatesRequired,
}: TwoStopQuoteSectionProps) => {
  const { formState, getFieldState } = formMethods;
  const { defaultValues, dirtyFields } = formState;

  const { aiDefaultValues } = useExtendedFormContext();
  const isTql = useIsTqlInbox();
  const pickupLabel = isTql ? 'Pick' : 'Pickup';
  const dropoffLabel = isTql ? 'Drop' : 'Dropoff';

  const hasAIHint = (fieldName: string): boolean => {
    const defaultValue = get(defaultValues, fieldName);
    const fieldState = getFieldState(fieldName as any);
    const isValid = !fieldState.isDirty && !get(dirtyFields, fieldName);

    return !!(aiDefaultValues && defaultValue && isValid);
  };

  useEffect(() => {
    if (isFTLQuickQuoteDatesRequired && !hasAddedQuoteDates) {
      setHasAddedQuoteDates(true);
    }
  }, [isFTLQuickQuoteDatesRequired, hasAddedQuoteDates, setHasAddedQuoteDates]);

  const pickupHasAIHint = hasAIHint('stops.0.location');
  const dropoffHasAIHint = hasAIHint('stops.1.location');

  return (
    <>
      <Flex justify='between' align='start' gap='md' className='w-full mx-0'>
        {/* Pickup */}
        <div className='w-full'>
          <QuickQuoteLocationInput
            name='stops.0.location'
            label={pickupLabel}
            placeholder='City, State or ZIP'
            inputClassName='placeholder:text-[12px]'
            required
          />
        </div>

        {!pickupHasAIHint && !dropoffHasAIHint && (
          <ArrowRightIcon className='w-10 text-neutral-500 mt-7' />
        )}

        {/* Dropoff */}
        <div className='w-full'>
          <QuickQuoteLocationInput
            name='stops.1.location'
            label={dropoffLabel}
            placeholder='City, State or ZIP'
            inputClassName='placeholder:text-[12px]'
            required
          />
        </div>
      </Flex>

      {hasAddedQuoteDates && (
        <Flex direction='col' gap='md'>
          <Typography variant='body' weight='semibold'>
            Dates
          </Typography>
          <Flex justify='between' align='end' gap='lg' className='w-full mx-0'>
            <div className='w-full'>
              <Label name='pickupDate'>{pickupLabel}</Label>
              <QuickQuoteDatePicker
                name='pickupDate'
                rules={
                  isFTLQuickQuoteDatesRequired ? { required: 'Required' } : {}
                }
              />
            </div>

            <div className='w-full'>
              <Label name='deliveryDate'>{dropoffLabel}</Label>
              <QuickQuoteDatePicker
                name='deliveryDate'
                rules={
                  isFTLQuickQuoteDatesRequired ? { required: 'Required' } : {}
                }
              />
            </div>
          </Flex>
        </Flex>
      )}

      {!hasAddedQuoteDates && (
        <AddDateDetailsButton
          buttonNamePosthog={ButtonNamePosthog.AddQuoteDateDetails}
          onClick={() => setHasAddedQuoteDates(true)}
        />
      )}

      {showKencoUrgency && <KencoUrgencyPicker control={formMethods.control} />}
    </>
  );
};

const KencoUrgencyPicker = ({
  control,
}: {
  control: Control<QuickQuoteInputs>;
}) => {
  return (
    <Controller
      name='kencoUrgency'
      control={control}
      render={({ field }) => {
        const selected: KencoUrgency = field.value ?? KencoUrgency.CanGet;
        return (
          <Flex
            justify='between'
            align='center'
            className='w-full flex-wrap gap-y-2 mt-3'
          >
            <Typography variant='body-sm' weight='medium'>
              Urgency
            </Typography>
            <Flex
              role='radiogroup'
              align='center'
              gap='lg'
              className='flex-wrap justify-end shrink-0'
              aria-label='Kenco Flash tender type'
            >
              {Object.values(KencoUrgency).map((opt) => (
                <label
                  key={opt}
                  className='inline-flex items-center gap-2 cursor-pointer text-sm text-neutral-800'
                >
                  <input
                    type='radio'
                    className='h-4 w-4 border-neutral-300 text-brand-500 focus:ring-brand-500'
                    checked={selected === opt}
                    onChange={() => field.onChange(opt)}
                  />
                  {KencoUrgencyLabel[opt]}
                </label>
              ))}
            </Flex>
          </Flex>
        );
      }}
    />
  );
};

// Show Kenco Urgency only if pickup date is within the next 2 days
export function shouldShowKencoUrgencyForPickup(
  pickupDate: MaybeUndef<Date>,
  now: Date = new Date()
): boolean {
  if (!pickupDate || Number.isNaN(pickupDate.getTime())) {
    return false;
  }

  return dayjs(pickupDate)
    .startOf('day')
    .isBefore(dayjs(now).startOf('day').add(2, 'day'));
}
